import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    createDailyTruckPlanned,
    DailyTruckPlanned,
    deleteDailyTruckPlanned,
    updateDailyTruckPlanned,
} from '../../services/apiSLTP/dailyReports/dailyTrucksPlanned';
import { DailyTransfer } from '../../services/apiSLTP/dailyReports/dailyTransfers';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import { getMaterialStateColor } from '../../utils/colors';
import { getTruckModeInfos, materialState, stepModes, truckGroupEnum, truckMode } from '../../utils/enums';
import DraggableCell from '../DraggableCell';
import DroppableCell from '../DroppableCell';
import { useTheme } from '../../hooks/useTheme';
import { Box, Divider, Slider, Typography } from '@mui/material';
import { useUpdateDailyTransferMutation } from '../../hooks/mutations/useDailyTransfersMutation';
import { DataItem } from '../../services/apiSLTP/activities';
import { isTruckContextEnabled } from '../../constants/helpers/truck';

interface TruckPlannedItemProps {
    truckPlanned: DailyTruckPlanned;
    index: number;
    activityId: string;
    activityReportTruckGroup?: number;
    stepMode: number;
    disabled: boolean;
    group?: number;
    isSpecial?: boolean;
    refetch: () => void;
    setShowModal: (v: boolean) => void;
    setSelectedId: (id: string) => void;
    setShowAssignModal: (v: boolean) => void;
    setSelectedTruckPlanned: (t: DailyTruckPlanned | null) => void;
    setShowTransferModal: (v: boolean) => void;
    setSelectedTransfer: (t: DailyTransfer | null) => void;
    updateTruckPlannedRankUpDown: (params: { id: string; body: { action: 'up' | 'down' } }) => void;
    handleCreateDailyTruckPlanned: (id: string) => Promise<void>;
    activities: DataItem[];
}

const TruckPlannedItem: React.FC<TruckPlannedItemProps> = ({
    truckPlanned,
    index,
    activityId,
    stepMode,
    disabled,
    activityReportTruckGroup,
    group,
    refetch,
    setShowModal,
    setSelectedId,
    setShowAssignModal,
    setSelectedTruckPlanned,
    setShowTransferModal,
    setSelectedTransfer,
    updateTruckPlannedRankUpDown,
    isSpecial,
    handleCreateDailyTruckPlanned,
    activities,
}) => {
    const { t } = useTranslation();
    const { muiTheme } = useTheme();
    const [rank, setRank] = useState(truckPlanned.rank || 1);
    const itemType2 = 'TRANSFERSPLANNED';
    const MIN = 1;
    const MAX = 20;

    const { mutate: updateDailyTransferPlanned } = useUpdateDailyTransferMutation();

    const mode = truckPlanned.mode ?? -1;

    const handleTransferPlannedChange = async (fromLineId: string, fromItemId: string, toLineId: string) => {
        if (fromLineId === toLineId) return;
        try {
            updateDailyTransferPlanned(
                { DailyTransferId: fromItemId, data: { dailyTrucksPlannedId: toLineId } },
                {
                    onSuccess: async () => {
                        const tmpTruckPlanned = activities.flatMap((value) => value.dailyTrucksPlanned).find((value) => value._id === fromLineId);
                        if (tmpTruckPlanned && tmpTruckPlanned.mode === truckMode.TRANSFER && tmpTruckPlanned.transfers.length <= 1)
                            await deleteDailyTruckPlanned(fromLineId);
                        refetch();
                    },
                }
            );
        } catch (error) {
            console.error('Error updating truck planned:', error);
        }
    };

    useEffect(() => {
        setRank(truckPlanned.rank || 1);
    }, [truckPlanned.rank]);

    const renderFromBlock = () =>
        (truckPlanned.departureDumpId || truckPlanned.departureLoadNatureId || truckPlanned.departureSiteId || truckPlanned.departureQuarryId) && (
            <div className="fx-c pad-l-3px border-radius-10px m-b-5px" style={{ backgroundColor: '#EEE' }}>
                {(truckPlanned.departureDumpId || truckPlanned.departureLoadNatureId) && (
                    <div className="" style={{ fontStyle: 'italic' }}>
                        {truckPlanned.departureDumpId && `${capitalizeFirstLetter(t('dump'))} ${truckPlanned.departureDumpId?.designation} `}
                        {truckPlanned.departureLoadNatureId && `${truckPlanned.departureLoadNatureId.designation}`}
                    </div>
                )}
                {(truckPlanned.departureSiteId || truckPlanned.departureQuarryId) && (
                    <div className="one-line">
                        <b>{t('from') + ' : '}</b>
                        {truckPlanned.departureSiteId?.name || truckPlanned.departureQuarryId?.designation}
                    </div>
                )}
            </div>
        );

    const renderToBlock = () =>
        (truckPlanned.arrivalDumpId || truckPlanned.arrivalLoadNatureId || truckPlanned.arrivalSiteId || truckPlanned.arrivalQuarryId) && (
            <div className="fx-c pad-l-3px border-radius-10px m-b-5px" style={{ backgroundColor: '#EEE' }}>
                {(truckPlanned.arrivalDumpId || truckPlanned.arrivalLoadNatureId) && (
                    <div className="one-line" style={{ fontStyle: 'italic' }}>
                        {truckPlanned.arrivalDumpId && `${capitalizeFirstLetter(t('dump'))} ${truckPlanned.arrivalDumpId?.designation} `}
                        {truckPlanned.arrivalLoadNatureId && `${truckPlanned.arrivalLoadNatureId.designation}`}
                    </div>
                )}
                {(truckPlanned.arrivalSiteId || truckPlanned.arrivalQuarryId) && (
                    <div className="one-line">
                        <b>{t('to') + ' : '}</b>
                        {truckPlanned.arrivalSiteId?.name || truckPlanned.arrivalQuarryId?.designation}
                    </div>
                )}
            </div>
        );

    const renderTransfers = () =>
        truckPlanned.mode === truckMode.TRANSFER &&
        truckPlanned.transfers.map((transfer) => (
            <div
                key={transfer._id}
                className="fx-c fx-items-start border-radius-10px smFont pad-l-3px m-b-5px"
                style={{ backgroundColor: getMaterialStateColor(transfer.state), gap: 5 }}
            >
                <DraggableCell
                    dragIds={{ itemId: transfer._id, lineId: truckPlanned._id }}
                    index={index}
                    itemType={itemType2}
                    disabled={disabled}
                    disableMenu
                    state={transfer.state}
                    onDoubleClick={() => {
                        setShowTransferModal(true);
                        setSelectedTransfer(transfer);
                        setSelectedId(truckPlanned._id);
                    }}
                >
                    <div className="fx-c fx-col gap-2 one-line-container">
                        {(transfer.machineId?.designation ||
                            transfer.toolId?.designation ||
                            transfer.rentalName ||
                            transfer.rentalCompanyId?.designation) && (
                            <div className="one-line" style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {transfer.machineId?.designation ||
                                    transfer.toolId?.designation ||
                                    transfer.rentalName + ' ' + (transfer.rentalCompanyId?.designation || '')}
                            </div>
                        )}
                        <div className="one-line" style={{ flex: 1 }}>
                            <b>{transfer.state === materialState.NEW ? t('from') + ' : ' : t('to') + ' : '}</b>
                            {transfer.siteId?.name || transfer.quarryId?.designation}
                        </div>
                    </div>
                </DraggableCell>
            </div>
        ));

    return (
        <div className="fx-r gap-5px">
            <DraggableCell
                dragIds={{ itemId: truckPlanned._id, lineId: activityId }}
                index={index}
                itemType="TRUCKSPLANNED"
                onDelete={async () => {
                    await deleteDailyTruckPlanned(truckPlanned._id);
                    refetch();
                }}
                onDuplicate={async () => {
                    await handleCreateDailyTruckPlanned(truckPlanned._id);
                    refetch();
                }}
                onDoubleClick={() => {
                    setShowModal(true);
                    setSelectedId(truckPlanned._id);
                }}
                disabled={disabled}
                menuItems={[
                    {
                        action: () => {
                            setSelectedTruckPlanned(truckPlanned);
                            setShowAssignModal(true);
                        },
                        label: capitalizeFirstLetter(t('assignRide')),
                        shortcut: '',
                    },
                ]}
            >
                <DroppableCell
                    dropId={{ lineId: truckPlanned._id }}
                    moveCell={handleTransferPlannedChange}
                    itemType={itemType2}
                    disabled={disabled || mode !== truckMode.TRANSFER}
                >
                    <div className="h-100 one-line-container">
                        <div className="one-line font-w-700">
                            {stepMode === stepModes.PLANNING && activityReportTruckGroup === truckGroupEnum.AMPLIROLL && (
                                <span className="border-radius-10px bg-white pad-l-3px pad-r-3px m-r-5px">
                                    {'№' + rank.toString().padStart(2, '0')}
                                </span>
                            )}
                            {(truckPlanned.trucksAmount !== 1 || isSpecial) && truckPlanned.trucksAmount + ''}
                            {typeof truckPlanned.initialTrucksAmount === 'number' &&
                                truckPlanned.initialTrucksAmount > 1 &&
                                isSpecial &&
                                '/' + truckPlanned.initialTrucksAmount + ''}
                            {` ${truckPlanned.truckTypeId.designation} ${truckPlanned.truckCompanyId?.designation || ''} ${truckPlanned.truckId?.designation && truckPlanned.truckCompanyId?.isOwnCompany ? formatName(truckPlanned.truckId.userId?.firstName, truckPlanned.truckId.userId?.lastName) : ''}`}
                        </div>
                        {/*truckPlanned.truckId && <div className="one-line">{truckPlanned.truckId.designation}</div>*/}
                        <div className="one-line" style={{ fontStyle: 'italic' }}>
                            {mode !== -1 && `${capitalizeFirstLetter(t(getTruckModeInfos(mode)))} `}
                            {truckPlanned.ridesAmount ? ` | ${truckPlanned.ridesAmount} ${t('ride(s)')}` : ''}
                        </div>

                        {isTruckContextEnabled(mode, 'departure') && renderFromBlock()}
                        {isTruckContextEnabled(mode, 'arrival') && renderToBlock()}
                        {isTruckContextEnabled(mode, 'transfer') && renderTransfers()}
                        {truckPlanned.observations && <div className="one-line">{truckPlanned.observations}</div>}

                        {!disabled && activityReportTruckGroup === truckGroupEnum.AMPLIROLL && (
                            <div className="fx-r  fx-items-c gap-10px">
                                <Typography
                                    variant="body2"
                                    onClick={async () => {
                                        setRank(MIN);
                                        await updateDailyTruckPlanned(truckPlanned._id, { rank: MIN });
                                        refetch();
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {MIN}
                                </Typography>
                                <Slider
                                    size="small"
                                    value={rank}
                                    onChange={(e, value) => setRank(value as number)}
                                    onChangeCommitted={async (e, value) => {
                                        await updateDailyTruckPlanned(truckPlanned._id, { rank: value });
                                        refetch();
                                    }}
                                    min={1}
                                    max={20}
                                    step={1}
                                    marks
                                    valueLabelDisplay="auto"
                                />
                                <Typography
                                    variant="body2"
                                    onClick={async () => {
                                        setRank(MAX);
                                        await updateDailyTruckPlanned(truckPlanned._id, { rank: MAX });
                                        refetch();
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {MAX}
                                </Typography>
                            </div>
                        )}
                    </div>
                </DroppableCell>
            </DraggableCell>
        </div>
    );
};

export default TruckPlannedItem;
